import { useEffect, useRef, useState, type ReactNode } from 'react'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { estadosYCiudades } from '@/features/rh/api/estadosYCiudades'
import {
  buscarPorNombre,
  separarResidencia,
  textoDeResidencia,
  type CiudadDeMexico,
  type EstadoDeMexico,
} from '@/features/rh/lib/ciudadDeResidencia'

interface SelectorDeResidenciaProps {
  /** El texto guardado, «Monterrey, Nuevo León», o vacío. */
  valor: string
  onChange: (texto: string) => void
  idBase: string
  /** Cada pantalla pinta su etiqueta a su manera (asterisco, «opcional»...). */
  etiqueta: (htmlFor: string, texto: string) => ReactNode
  /** Clase del campo de texto al que se vuelve si el catalogo no carga. */
  claseInput: string
  error?: string
}

/**
 * Estado y ciudad de residencia para las pantallas de RH que editan un
 * candidato. Entrega un solo texto, igual que el campo de antes.
 *
 * Al editar entiende lo ya guardado: si el texto es «Ciudad, Estado» y existe
 * en el catalogo, deja ambos elegidos. Si es un valor de antes del selector
 * (por ejemplo «monterrey nl») lo muestra y lo conserva hasta que RH elija
 * otro: no se borra por abrir la pantalla.
 *
 * Los campos son opcionales: la X deja el valor vacio.
 */
export const SelectorDeResidencia = ({
  valor,
  onChange,
  idBase,
  etiqueta,
  claseInput,
  error,
}: SelectorDeResidenciaProps) => {
  const [estados, setEstados] = useState<EstadoDeMexico[]>([])
  const [estadoId, setEstadoId] = useState('')
  const [ciudades, setCiudades] = useState<CiudadDeMexico[]>([])
  const [ciudadId, setCiudadId] = useState('')
  const [cargandoCiudades, setCargandoCiudades] = useState(false)
  const [valorAnterior, setValorAnterior] = useState('')
  // Si el catalogo no responde se vuelve al campo de texto de antes: perder la
  // captura por un desplegable que no carga seria el peor intercambio.
  const [catalogoNoDisponible, setCatalogoNoDisponible] = useState(false)

  // El ultimo texto que salio de aqui. Sirve para distinguir un cambio hecho
  // por este componente de uno que llega de fuera (datos que terminan de cargar).
  const ultimoEmitido = useRef<string | null>(null)
  // Descarta la respuesta de ciudades de un estado que ya no esta elegido.
  const peticion = useRef(0)

  useEffect(() => {
    let vigente = true
    estadosYCiudades
      .estados()
      .then((lista) => vigente && setEstados(lista))
      .catch(() => vigente && setCatalogoNoDisponible(true))
    return () => {
      vigente = false
    }
  }, [])

  const emitir = (texto: string) => {
    ultimoEmitido.current = texto
    onChange(texto)
  }

  const cargarCiudades = (id: number): Promise<CiudadDeMexico[]> => {
    const esta = ++peticion.current
    setCargandoCiudades(true)
    return estadosYCiudades
      .ciudades(id)
      .then((lista) => (esta === peticion.current ? lista : []))
      .catch(() => {
        setCatalogoNoDisponible(true)
        return []
      })
      .finally(() => {
        if (esta === peticion.current) setCargandoCiudades(false)
      })
  }

  // Lo que llega de fuera: se interpreta contra el catalogo.
  useEffect(() => {
    if (estados.length === 0 || valor === ultimoEmitido.current) return
    ultimoEmitido.current = valor

    const limpio = valor.trim()
    const par = limpio ? separarResidencia(limpio) : null
    const estado = par ? buscarPorNombre(estados, par.estado) : undefined

    if (!par || !estado) {
      setEstadoId('')
      setCiudadId('')
      setCiudades([])
      setValorAnterior(limpio)
      return
    }

    setEstadoId(String(estado.id))
    setCiudadId('')
    void cargarCiudades(estado.id).then((lista) => {
      const ciudad = buscarPorNombre(lista, par.ciudad)
      setCiudades(lista)
      setCiudadId(ciudad ? String(ciudad.id) : '')
      setValorAnterior(ciudad ? '' : limpio)
    })
  }, [valor, estados])

  const elegirEstado = (id: string) => {
    setEstadoId(id)
    setCiudades([])
    // Si habia una ciudad elegida, el texto ya no corresponde al estado nuevo.
    // Un valor de antes del selector se conserva hasta que se elija ciudad.
    if (ciudadId) {
      setCiudadId('')
      emitir('')
    }
    if (id) void cargarCiudades(Number(id)).then(setCiudades)
  }

  const elegirCiudad = (id: string) => {
    setCiudadId(id)
    const ciudad = ciudades.find((c) => String(c.id) === id)
    const estado = estados.find((e) => String(e.id) === estadoId)
    setValorAnterior('')
    emitir(ciudad && estado ? textoDeResidencia(ciudad.name, estado.name) : '')
  }

  const usaCatalogo = estados.length > 0 && !catalogoNoDisponible

  if (!usaCatalogo) {
    return (
      <div>
        {etiqueta(`${idBase}-ciudad`, 'Ciudad de residencia')}
        <input
          id={`${idBase}-ciudad`}
          type="text"
          maxLength={120}
          value={valor}
          onChange={(e) => emitir(e.target.value)}
          className={claseInput}
          placeholder="Ej: Monterrey, Nuevo León"
        />
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      </div>
    )
  }

  return (
    <>
      <div>
        {etiqueta(`${idBase}-estado`, 'Estado de residencia')}
        <SelectBuscable
          variante="formulario"
          id={`${idBase}-estado`}
          name={`${idBase}-estado`}
          value={estadoId}
          onChange={elegirEstado}
          placeholder="Selecciona un estado"
          placeholderDeBusqueda="Buscar estado..."
          opciones={estados.map((e) => ({ value: String(e.id), label: e.name }))}
        />
      </div>
      <div>
        {etiqueta(`${idBase}-ciudad`, 'Ciudad de residencia')}
        <SelectBuscable
          variante="formulario"
          id={`${idBase}-ciudad`}
          name={`${idBase}-ciudad`}
          value={ciudadId}
          onChange={elegirCiudad}
          disabled={!estadoId || cargandoCiudades}
          error={Boolean(error)}
          placeholder={
            !estadoId
              ? 'Primero elige un estado'
              : cargandoCiudades
                ? 'Cargando ciudades...'
                : 'Selecciona una ciudad'
          }
          placeholderDeBusqueda="Buscar ciudad..."
          opciones={ciudades.map((c) => ({ value: String(c.id), label: c.name }))}
        />
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        {valorAnterior && !ciudadId && (
          <p className="text-xs text-gray-500 mt-1">
            Guardado: «{valorAnterior}». Elige estado y ciudad para reemplazarlo.
          </p>
        )}
      </div>
    </>
  )
}
